import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from 'react/jsx-runtime'
import {
    addErrorNotification,
    getAdvanceBookingPeriodForShowtime,
    getAttributesByIds,
    getFetchedShowtimeById,
    getSiteById,
    groupArrayBy,
    isFetchedDataValid,
    isNonEmptyArray,
    NotificationType,
} from '@vista/omnichannel-components-domain'
import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { isFuture, isToday } from 'date-fns'
import { observer } from 'mobx-react'
import { useContext } from 'react'
import { FilmAdvanceBookingPeriodNotificationUI } from '../../../components/film/advance-booking-period-notification/subcomponents/ui'
import { useOccContext } from '../../../context/occ/hooks/use-occ-context'
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification'
import { AttributeList } from '../../attribute-list/attribute-list'
import { getCommonAttributes } from '../../attribute-list/utils'
import { DisplayText } from '../../display-text/display-text'
import { HeadingLevel } from '../../heading/heading'
import { ShowtimeList } from '../../showtime-list/showtime-list'
import { ShowtimePickerContextInstance } from '../types/context'
import { ShowtimePickerSiteDetails } from './site-details'
const b = bemClassBuilder('v-showtime-picker-site')
export const ShowtimePickerSite = observer(props => {
    var _a
    const { i18n, assets: occAssets } = useOccContext()
    const { siteId, showtimes, filmId } = props
    const site = getSiteById(siteId)
    if (!site) return null
    const { displayText, cdnOptions, config, getNoShowtimesForSiteDisplayTextProps, groupShowtimesByDate, events } =
        useContext(ShowtimePickerContextInstance)
    const { filmDetails, showSiteDetails } = config
    const allAttributes = showtimes.map(showtime => getAttributesByIds(showtime.attributeIds))
    const commonAttributes = getCommonAttributes(allAttributes)
    const siteHeadingLevel = filmDetails.show ? HeadingLevel.H4 : HeadingLevel.H3
    const showtimeListHeadingLevel = filmDetails.show ? HeadingLevel.H5 : HeadingLevel.H4
    const hasShowtimes = isNonEmptyArray(showtimes)
    // The booking period will be the same for all showtime for the same film and site.
    // The booking period will be the same for all showtimes for the same film and site.
    const isValidShowtime = hasShowtimes && isFetchedDataValid(getFetchedShowtimeById(showtimes[0].id))
    const bookingPeriod =
        hasShowtimes && isValidShowtime ? getAdvanceBookingPeriodForShowtime(showtimes[0].id) : undefined
    // Notifications will be the same for all showtimes for the same film and site. Use the same ID to prevent duplicates.
    const bookingPeriodNotificationId = getIdForComponentNotification(
        'showtime-picker',
        NotificationType.Error,
        `booking-period-${filmId}-${siteId}`
    )
    const noShowtimesDisplayTextProps =
        (_a =
            getNoShowtimesForSiteDisplayTextProps === null || getNoShowtimesForSiteDisplayTextProps === void 0
                ? void 0
                : getNoShowtimesForSiteDisplayTextProps(site.id, filmId)) !== null && _a !== void 0
            ? _a
            : {
                  label: displayText.noShowtimesForAnyDateTitle,
              }
    const siteName = i18n.translatable(site.name)
    const handleShowtimeClicked = showtime => {
        var _a
        if (bookingPeriod && isFuture(new Date(bookingPeriod.startsAt))) {
            addShowtimeUnavailableErrorNotification(
                displayText,
                occAssets,
                siteName,
                bookingPeriodNotificationId,
                bookingPeriod,
                i18n
            )
            return
        }
        ;(_a = events.onShowtimeClicked) === null || _a === void 0 ? void 0 : _a.call(events, showtime)
    }
    const getShowtimePickerShowtimeList = (validShowtimes, heading, key) =>
        _jsx(
            ShowtimeList,
            {
                showtimes: validShowtimes,
                hiddenAttributeIds: commonAttributes.map(attr => attr.id),
                ianaTimeZoneName: site.ianaTimeZoneName,
                displayText: Object.assign(Object.assign({}, displayText), { heading }),
                config: Object.assign(Object.assign({}, config), { headingLevel: showtimeListHeadingLevel }),
                cdnOptions: cdnOptions,
                onShowtimeClicked: handleShowtimeClicked,
            },
            key
        )
    const getGroupedShowtimesList = () => {
        if (!groupShowtimesByDate) return getShowtimePickerShowtimeList(showtimes)
        const showtimesByBusinessDate = groupArrayBy(showtimes, showtime => showtime.schedule.businessDate)
        const groupedShowtimeList = Object.keys(showtimesByBusinessDate).map(businessDate => {
            const formattedBusinessDate = i18n.template(
                displayText.businessDateFormat,
                { businessDate },
                { date: { ianaTimeZoneName: site.ianaTimeZoneName } }
            )
            return getShowtimePickerShowtimeList(
                showtimesByBusinessDate[businessDate],
                formattedBusinessDate,
                businessDate
            )
        })
        return groupedShowtimeList
    }

    /*
     * HACK: siteIds prop iterate out to site.js  per showtime film. Added check to see whether site has a screen date.
     * if not emptySiteClass is 'empty-site' otherwise  ''.
     * */
    const emptySiteClass =
        !hasShowtimes && noShowtimesDisplayTextProps.label === displayText.noShowtimesForSiteAndFilmMessage
            ? ' empty-site'
            : ''

    return _jsxs(
        'div',
        Object.assign(
            // HACK: Append emptySiteClass
            { className: b.block() + emptySiteClass },
            {
                children: [
                    showSiteDetails && _jsx(ShowtimePickerSiteDetails, { site: site, headingLevel: siteHeadingLevel }),
                    commonAttributes.length > 0 &&
                        _jsx(
                            'div',
                            Object.assign(
                                { className: b.element('common-attributes') },
                                {
                                    children: _jsx(AttributeList, {
                                        attributes: commonAttributes,
                                        heading: displayText.commonAttributesListLabel,
                                        showDetails: false,
                                        iconCdnOptions: cdnOptions.attributeIcon,
                                    }),
                                }
                            )
                        ),
                    hasShowtimes
                        ? _jsxs(_Fragment, {
                              children: [
                                  _jsx(FilmAdvanceBookingPeriodNotificationUI, {
                                      id: bookingPeriodNotificationId,
                                      bookingPeriod: bookingPeriod,
                                      displayText: {
                                          messageFormat: displayText.advanceBookingPeriodForSiteNotificationFormat,
                                          todayMessageFormat:
                                              displayText.advanceBookingPeriodForSiteTodayNotificationFormat,
                                      },
                                      templateParameters: { siteName },
                                  }),
                                  getGroupedShowtimesList(),
                              ],
                          })
                        : _jsx(
                              DisplayText,
                              Object.assign({}, noShowtimesDisplayTextProps, {
                                  className: b.element('empty-list-label'),
                              })
                          ),
                ],
            }
        )
    )
})
ShowtimePickerSite.displayName = 'ShowtimePickerSite'
const addShowtimeUnavailableErrorNotification = (
    displayText,
    occAssets,
    siteName,
    notificationId,
    bookingPeriod,
    i18n
) => {
    const { startsAt } = bookingPeriod
    const template = isToday(new Date(startsAt))
        ? displayText.showtimeUnavailableAdvanceBookingPeriodTodayNotificationFormat
        : displayText.showtimeUnavailableAdvanceBookingPeriodNotificationFormat
    addErrorNotification({
        id: notificationId,
        iconName: occAssets.advanceBookingPeriodNotificationIconName,
        messages: [i18n.template(template, { siteName, startsAt })],
    })
}
